import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  COLORS,
  GenericIconName,
  Icon,
  Tooltip,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React from 'react';
import sc from 'styled-components';

interface SaveFilterButtonProps {
  openYourFiltersDropdown: (showSavedSuccessMessage?: boolean) => void;
  isYourFiltersDropdownOpen: boolean;
  currentFilterCombinationAlreadyExists: boolean;
  savePersonalizedFilter: VoidFunction;
}

export const SaveFilterButton: React.FC<SaveFilterButtonProps> = ({
  openYourFiltersDropdown,
  isYourFiltersDropdownOpen,
  currentFilterCombinationAlreadyExists,
  savePersonalizedFilter,
}) => {
  const {t} = useTranslation();

  const handleOnClick = () => {
    if (!currentFilterCombinationAlreadyExists) {
      savePersonalizedFilter();
      openYourFiltersDropdown(true);
    }
  };

  return (
    <ConditionalTooltip showTooltip={currentFilterCombinationAlreadyExists}>
      <Wrapper>
        <StyledButton
          data-testid="saveFilterButton"
          data-disabled={
            currentFilterCombinationAlreadyExists || isYourFiltersDropdownOpen
          }
          onClick={handleOnClick}
          aria-label={undefined}
        >
          <StyledIcon asset={GenericIconName.AddCircleFill} size="XSS" />
          {t('Save Filter')}
        </StyledButton>
      </Wrapper>
    </ConditionalTooltip>
  );
};

const ConditionalTooltip: React.FC<
  React.PropsWithChildren<{showTooltip?: boolean}>
> = ({children, showTooltip}) => {
  const {t} = useTranslation();

  if (showTooltip) {
    return (
      <Tooltip
        label={t('The filter combination is already saved')}
        placement="bottom-center"
        theme="light"
        borderColor="Grey3"
      >
        {children}
      </Tooltip>
    );
  }

  return <>{children}</>;
};

const Wrapper = sc.div`
  display: flex;
`;

const StyledButton = sc(Button)`
  display: flex;
  align-items: center;
  color: ${COLORS.Black};
  background-color: ${COLORS.Grey4};
  padding: 0.5rem 1.125rem;
  margin-left: 1rem;
  border-radius: 3px;

  &:hover {
    background-color: ${COLORS.Green};
  }

  &[data-disabled='true'] {
    pointer-events: none;
    cursor: pointer;
    background-color: ${COLORS.Grey5};
  }
`;

const StyledIcon = linariaStyled(Icon)`
  margin-right: 4px;
`;
